import type { Asset } from "../api/types.js";
import { basename, formatBytes, isVideoPath, thumbnailLabel } from "./asset-helpers.js";
import { AssetThumbnail } from "./asset-thumbnail.js";
import {
  DEFAULT_GRID_ASPECT,
  DEFAULT_GRID_ZOOM,
  GRID_ASPECT_CLASS,
  GRID_ZOOM_LEVELS,
  type GridAspect,
  type GridZoom,
} from "./grid-zoom-slider.js";

// 1.D.5 Grid-naekyma asseteille. Tiilet: thumbnail (2.C.6) + alle
// tiedostonimi + meta-rivi (koko + tier).
//
// 2.D.3 — multi-select-tuki: jos `selectedIds` annettu, naytetaan
// checkbox-overlay tiilen kulmassa. Klikkaus tile:lle togglaa valinnan
// (pohjautuu siihen onko jo valittu); jos ei valittuja yhtaeaen, klikkaus
// avaa modalin (onSelect-callback).
//
// UM.8 — zoom-slider valitsee tile:n minWidth:n ja thumbnail-pyynnoen
// size:n. Aspect-ratio kiintea 4:3 (valokuvaajan materiaali on yleensae
// vaakamuotoista mutta ei aivan 16:9 mihin video taipuu).

export type AssetGridProps = {
  assets: Asset[];
  onSelect?: (asset: Asset) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (asset: Asset, e: React.MouseEvent) => void;
  /** XS=0 ... XL=4. Default M=2. */
  zoom?: GridZoom;
  /** Aspect-ratio tilen muodolle. Default 4:3. */
  aspect?: GridAspect;
};

export function AssetGrid({
  assets,
  onSelect,
  selectedIds,
  onToggleSelect,
  zoom = DEFAULT_GRID_ZOOM,
  aspect = DEFAULT_GRID_ASPECT,
}: AssetGridProps) {
  if (assets.length === 0) {
    return <p className="text-slate-500">Ei assettia naytettavaksi.</p>;
  }
  const selectionMode = selectedIds && selectedIds.size > 0;
  const { minWidth, imgSize } = GRID_ZOOM_LEVELS[zoom];
  // Pienemmillae kooilla nayttotekstit pakkautuvat ilman meta-rivia.
  const showMeta = zoom >= 1;
  const aspectClass = GRID_ASPECT_CLASS[aspect];
  return (
    <ul
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))` }}
    >
      {assets.map((a) => {
        const isSelected = selectedIds?.has(a.id) ?? false;
        return (
          <li key={a.id} className="relative">
            <button
              type="button"
              onClick={(e) => {
                if (selectionMode || e.metaKey || e.ctrlKey || e.shiftKey) {
                  onToggleSelect?.(a, e);
                } else {
                  onSelect?.(a);
                }
              }}
              className={
                "group flex w-full flex-col gap-2 rounded-md border p-2 text-left " +
                (isSelected
                  ? "border-sky-500 bg-sky-950/40"
                  : "border-slate-800 bg-slate-900/40 hover:bg-slate-900")
              }
            >
              <AssetThumbnail
                assetId={a.id}
                fallbackLabel={thumbnailLabel(a.currentPath)}
                size={imgSize}
                alt={basename(a.currentPath)}
                isVideo={isVideoPath(a.currentPath) || Boolean(a.videoCodec)}
                className={
                  "relative grid place-items-center overflow-hidden rounded bg-slate-800 text-lg font-medium text-slate-300 group-hover:bg-slate-700 " +
                  aspectClass
                }
              />
              <span className="block truncate text-sm text-slate-100">
                {basename(a.currentPath)}
              </span>
              {showMeta && (
                <span className="flex justify-between text-xs text-slate-500">
                  <span>{formatBytes(a.sizeBytes ?? null)}</span>
                  <span className="uppercase tracking-wide">{a.currentTier}</span>
                </span>
              )}
            </button>
            {/* Checkbox-overlay vasemmassa ylakulmassa */}
            {onToggleSelect && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect(a, e);
                }}
                aria-label={isSelected ? "Poista valinta" : "Valitse"}
                aria-pressed={isSelected}
                className={
                  "absolute left-3 top-3 grid h-6 w-6 place-items-center rounded border-2 text-xs " +
                  (isSelected
                    ? "border-sky-400 bg-sky-500 text-white"
                    : "border-slate-500 bg-slate-900/80 text-transparent hover:border-slate-300")
                }
              >
                ✓
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
